import {
  Checkbox,
  Button,
  Select,
  theme,
  Tooltip as AntdTooltip,
  Switch,
  Slider,
  Collapse,
} from "antd";
import {
  BarChartOutlined,
  ClearOutlined,
  DragOutlined,
  FontSizeOutlined,
  LoadingOutlined,
  PercentageOutlined,
  SwapOutlined,
  NumberOutlined,
  TableOutlined,
  CaretRightOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ComponentProps,
  Dispatch,
  SetStateAction,
} from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  LegendProps,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  TooltipProps,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import { useClickAnyWhere, useEffectOnce } from "usehooks-ts";
import { columnForAggregation } from "@vzbl/integrations";
import { getTextSize } from "src/fonts";
import { Flex } from "src/components/Flex";
import { useUniqueId } from "src/hooks/useUniqueId";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { asKey, fromKey } from "src/utils/fields";
import { attributeToTitle, formatValue } from "src/utils/strings";
import { sortSetNatural } from "src/utils/sort";
import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { usePopupContext } from "src/hooks/usePopup";
import {
  getConsistentColorPalette,
  rainbow,
  ColorTheme,
  toColorRampConfig,
} from "src/utils/colors-v2";
import { useColorScheme } from "src/hooks/useColorScheme";
import {
  SearchWindowConfig,
  SearchWindowInput,
  toSearchWindowConfig,
} from "src/search/components/inputs/SearchWindow";
import { SearchWindow } from "src/search/types";
import { SearchContext } from "src/search/useSearchContext";
import { ExploreContext } from "../useExploreContext";
import { InvalidGroupBys } from "./InvalidGroupBys";
import { useChartData } from "./useChartData";
import { VisualizationProps } from "./visualization-props";
import { valueToLabel } from "./common";
import { InvalidAggregations } from "./InvalidAggregations";
import {
  CompareWithConfig,
  CompareWithControls,
  toCompareWithConfig,
} from "./CompareWithControls";
import { ColorRampConfig, ColorRampPicker } from "./ColorRampPicker";
import {
  FormattingConfig,
  FormattingControls,
  hasTimeUnit,
  toFormattingConfig,
  TimeUnit,
} from "./FormattingControls";
import { ContentView } from "./useContentView";
import { EmptyResultExplanation } from "./EmptyResultExplanation";

const curves = [
  "basis",
  "basisClosed",
  "basisOpen",
  "bumpX",
  "bumpY",
  "bump",
  "linear",
  "linearClosed",
  "natural",
  "monotoneX",
  "monotoneY",
  "monotone",
  "step",
  "stepBefore",
  "stepAfter",
] as const;

type Config = SearchWindowConfig &
  ColorRampConfig &
  CompareWithConfig &
  FormattingConfig & {
    labels: boolean;
    legend: boolean;
    values: boolean;
    asPercent: boolean;
    vertical: boolean;
    bars: "split" | "stack" | "percent";
    invertGroups: boolean;
    displayType: "bars" | "lines" | "area";
    showGrid: boolean;
    showValueAxis: boolean;
    curveType: (typeof curves)[number];
    minSegmentSize: number;
    barGroupSpacing: number;
    barPadding: number;
    barBorderRadius: number;
    barBorderWidth: number;
    focusedStack: number | null;
    stackOrder: "top-down" | "bottom-up";
  };

// Helper function to format numbers with abbreviations (K, M)
const formatNumberAbbreviation = (value: any): string => {
  const num = Number(value);
  if (Number.isNaN(num) || value === null || value === undefined) {
    // Return non-numeric values as is (or handle specific cases like __VZBL::null__ if needed)
    return String(value);
  }

  if (Math.abs(num) >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (Math.abs(num) >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  // Optionally, format smaller numbers (e.g., rounding)
  // For now, return as string, letting formatValue handle further specifics
  return String(num);
};

const toConfig: (data: Config | any) => Config = (data) => ({
  ...toCompareWithConfig(data),
  ...toSearchWindowConfig(data),
  ...toColorRampConfig(data),
  ...toFormattingConfig(data),
  labels: typeof data?.labels === "boolean" ? data.labels : true,
  legend: typeof data?.legend === "boolean" ? data.legend : true,
  asPercent: typeof data?.asPercent === "boolean" ? data.asPercent : false,
  showGrid: typeof data?.showGrid === "boolean" ? data.showGrid : false,
  values: typeof data?.values === "boolean" ? data.values : true,
  vertical: typeof data?.vertical === "boolean" ? data.vertical : false,
  showValueAxis:
    typeof data?.showValueAxis === "boolean" ? data.showValueAxis : true,
  invertGroups:
    typeof data?.invertGroups === "boolean" ? data.invertGroups : false,
  bars: ["split", "stack", "percent"].includes(data?.bars)
    ? data.bars
    : "stack",
  displayType: ["bars", "lines", "area"].includes(data?.displayType)
    ? data?.displayType
    : "bars",
  curveType: curves.includes(data?.curveType) ? data.curveType : "monotone",
  minSegmentSize:
    typeof data?.minSegmentSize === "number"
      ? Math.max(0, data.minSegmentSize)
      : 2,
  barGroupSpacing:
    typeof data?.barGroupSpacing === "number"
      ? Math.min(100, Math.max(0, data.barGroupSpacing))
      : 25,
  barPadding:
    typeof data?.barPadding === "number"
      ? Math.min(100, Math.max(0, data.barPadding))
      : 2,
  barBorderRadius:
    typeof data?.barBorderRadius === "number"
      ? Math.min(20, Math.max(0, data.barBorderRadius))
      : 1,
  barBorderWidth:
    typeof data?.barBorderWidth === "number"
      ? Math.min(5, Math.max(0, data.barBorderWidth))
      : 1,
  focusedStack:
    typeof data?.focusedStack === "number" ? data.focusedStack : null,
  stackOrder: ["top-down", "bottom-up"].includes(data?.stackOrder)
    ? data.stackOrder
    : "bottom-up",
});

// Add debug wrapper for formatValue
const debugFormatValue = (
  value: any,
  type: string | undefined,
  unit: string | undefined,
  timeUnit?: TimeUnit | number
) => {
  return formatValue(value, type, unit, timeUnit);
};

export function Chart(props: VisualizationProps) {
  const { view, params, aggregations } = props.context;
  useEffectOnce(() => {
    view.setConfig(toConfig(view.config));
  });

  const config = toConfig(view.config);
  const windowSetter = useRef({
    fn(window: SearchWindow) {
      aggregations.setWindow(window);
    },
  });
  const window: SearchWindow = {
    limit: config.limit,
    order:
      config.sortBy === "default"
        ? undefined
        : [
            {
              by:
                config.sortBy === "aggregation"
                  ? props.context.params.state.aggregations!.at(0)!
                  : config.sortBy,
              direction: config.sortDirection,
            },
          ],
  };
  const windowValue = useRef(window);
  windowValue.current = window;
  const windowCheckpoint = JSON.stringify(window);
  const subUnsub = useRef({
    subscribe: aggregations.subscribe,
    unsubscribe: aggregations.unsubscribe,
  });
  subUnsub.current = {
    subscribe: aggregations.subscribe,
    unsubscribe: aggregations.unsubscribe,
  };
  useEffect(() => {
    const setWindow = windowSetter.current.fn;
    setWindow(windowValue.current);
    const unsub = subUnsub.current.unsubscribe;
    subUnsub.current.subscribe();
    return () => unsub();
  }, [windowCheckpoint]);

  if (
    params.selectors.countGroupBys(params.state) > 2 ||
    params.selectors.countGroupBys(params.state) < 1
  )
    return props.onRefuse ? (
      <Refused onRefuse={props.onRefuse} />
    ) : (
      <InvalidGroupBys
        numGroups={params.selectors.countGroupBys(params.state)}
        view={view}
        requiredGroups={{ min: 1, max: 2 }}
      />
    );

  if (params.selectors.countAggregations(params.state) > 1)
    return props.onRefuse ? (
      <Refused onRefuse={props.onRefuse} />
    ) : (
      <InvalidAggregations
        numAggregations={params.selectors.countAggregations(params.state)}
        view={view}
        minAggregations={1}
        maxAggregations={1}
      />
    );

  return <Ready {...config} context={props.context} />;
}

function Refused({ onRefuse }: { onRefuse(): void }) {
  useEffectOnce(() => onRefuse());
  return null;
}

function Ready({
  asPercent,
  bars,
  curveType,
  displayType,
  invertGroups,
  labels,
  legend,
  limit,
  showChangeAs,
  showGrid,
  showValueAxis,
  sortBy,
  sortDirection,
  theme: ramp,
  values,
  vertical,
  minSegmentSize,
  barGroupSpacing,
  barPadding,
  barBorderRadius,
  barBorderWidth,
  focusedStack,
  stackOrder,
  timeUnit,
  ...props
}: Config & { context: SearchContext & { view: ContentView } }) {
  const mode = useColorScheme();
  const { aggregations, params } = props.context;
  const { token } = theme.useToken();
  const {
    xKey,
    yKey,
    yKeys,
    data: rawData,
    labels: dataLabels,
    xType,
    yType,
    zType,
    yAxisType,
    xAxisType,
  } = useChartData(aggregations, {
    pruneZeros: false,
    nullToZero: true,
    invertGroups,
    vertical,
    asPercent: asPercent,
    hasCompare: params.state.compareWith?.method === "tuple",
    hasPercentChange: showChangeAs === "percentage",
    timeUnit: props.context.view.config?.timeUnit,
  });

  // Add index to data for tracking group positions
  const data = rawData.map((item, index) => ({
    ...item,
    groupIndex: index,
  })) as ((typeof rawData)[0] & { groupIndex: number })[];

  const [activeIdx, setActiveIndex] = useState(-1);
  const [accentColor, setAccentColor] = useState<string | undefined>();
  const [legendPin, setLegendPin] = useState<string | undefined>();
  const [legendHighlightx, setLegendHighlight] = useState<string | undefined>();
  const [hoveredGroupIndex, setHoveredGroupIndex] = useState<number | null>(
    null
  );
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const stackId = useUniqueId();
  const [filterOpen, setFilterOpen] = useState<false | undefined>(undefined);
  useEffect(() => {
    if (filterOpen === false) setFilterOpen(undefined);
  }, [filterOpen]);
  useEffect(() => {
    setActiveIndex(-1);
    setLegendPin(undefined);
  }, [params.serialized]);
  const sortedKeys = useMemo(() => {
    // Get all unique keys first
    const keys = [...sortSetNatural(new Set(yKeys))].filter(Boolean);

    // Sort keys based on their values in the current group
    const sorted = keys.sort((a, b) => {
      // Get values for current active/hovered group
      const groupIndex = hoveredGroupIndex ?? 0;
      const groupData = data[groupIndex] || data[0];
      if (!groupData) return 0;

      const aValue = Number(groupData[a]) || 0;
      const bValue = Number(groupData[b]) || 0;

      // For bottom-up: larger values should be at the bottom (first in array)
      // For top-down: larger values should be at the top (last in array)
      return stackOrder === "top-down"
        ? aValue - bValue // Ascending order - smaller values first (bottom)
        : bValue - aValue; // Descending order - larger values first (bottom)
    });

    // Debug logging
    const groupIndex = hoveredGroupIndex ?? 0;
    const groupData = data[groupIndex] || data[0];

    return sorted;
  }, [yKeys, stackOrder, data, hoveredGroupIndex]);
  const ilen = sortedKeys.length;
  const gblen = props.context.params.state.groupBys?.length || 0;

  // Generate consistent color palette for all group names
  const colorPalette = useMemo(() => {
    return getConsistentColorPalette(sortedKeys, ramp, mode);
  }, [sortedKeys, ramp, mode]);

  const rainbowFn = useCallback(
    (dataKey: string, muted?: boolean) => {
      // Use new simplified color system with total categories for smart saturation
      const baseColor = rainbow(dataKey, ramp, mode, sortedKeys.length);

      // Apply muting if requested
      if (muted) {
        return `${baseColor}80`; // Add 50% opacity for muted effect
      }

      return baseColor;
    },
    [ramp, mode, sortedKeys.length]
  );

  const [minValue, maxValue] = useMemo(() => {
    const [minValue, maxValue] = data.reduce(
      ([min_, max_], row) =>
        sortedKeys.reduce(
          ([min__, max__], key) => {
            return [
              Math.min(min__, Number(row[key])),
              Math.max(max__, Number(row[key])),
            ];
          },
          [min_, max_]
        ),
      [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]
    );
    return [Math.min(minValue, 0), Math.max(maxValue, 0)];
  }, [sortedKeys, data]);
  const maxLabelChars = useMemo(
    () =>
      data.reduce((max, row) => {
        const value = valueToLabel(
          formatValue(row[xKey], xType.type, xType.unit)
        );
        return Math.max(`${value}`.trim().length, max);
      }, 0),
    [data, xKey, xType]
  );
  const maxLegendLabelWidth = useMemo(
    () =>
      Math.min(
        200,
        yKeys.reduce(
          (max, yk) =>
            Math.max(
              getTextSize(
                `${valueToLabel(
                  formatValue(yk, yType.type, yType.unit)
                )}`.trim(),
                12,
                500
              ).width,
              max
            ),
          0
        )
      ),
    [yKeys, yType.type, yType.unit]
  );
  const popup = usePopupContext();
  const legendHighlight = legendHighlightx || legendPin;
  const legendLabelWidth = yKeys.length > 5 ? maxLegendLabelWidth : undefined;

  const handleBarMouseEnter = (dataKey: string, stackIndex: number) => {
    // Remove legend highlight update
    if (bars === "stack") {
      props.context.view.setConfig({
        ...props.context.view.config,
        focusedStack: stackIndex,
      });
    }
  };

  const handleBarMouseLeave = () => {
    // Remove legend highlight reset
    setHoveredGroupIndex(null);
    setHoveredCategory(null);
    setActiveIndex(-1);
    if (bars === "stack") {
      props.context.view.setConfig({
        ...props.context.view.config,
        focusedStack: null,
      });
    }
  };

  if (data.length === 0 && !aggregations.isLoading) {
    const explanation = aggregations.data?.meta?.explanation || [];
    return <EmptyResultExplanation explanation={explanation} />;
  }
  return (
    <DynamicCanvas>
      {({ width, height }) => {
        if (aggregations.isLoading)
          return (
            <div
              style={{
                width,
                height,
                position: "absolute",
                zIndex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingOutlined />
            </div>
          );

        const fontSize = 12;
        const charSize = 8;
        const gapRatio = 0.05;
        const labelSize = Math.min(maxLabelChars * charSize, 100);

        // Content-aware dynamic sizing calculation
        const totalLabels = data.length;

        // Calculate optimal label width based on actual content
        const longestLabel = Math.max(
          ...data.map((d) => {
            const labelValue = d[xKey];
            return typeof labelValue === "string"
              ? labelValue.length
              : String(labelValue || "").length;
          })
        );

        // Proportional space allocation - adapts to content needs
        const availableLabelWidth = width * 0.6; // 60% of chart width for labels
        const charLimit = Math.max(
          6,
          Math.min(30, Math.floor(availableLabelWidth / charSize))
        );
        const labelHeight = (fontSize * 4) / 3;
        const workingSpace = vertical ? height : width;

        // Dynamic minimum bar size based on container and density
        const baseMinBarSize = Math.max(12, Math.min(32, workingSpace / 50));
        const minBarSize = Math.floor(
          baseMinBarSize * Math.max(0.7, 1 - totalLabels / 100)
        );
        const barGap = Math.floor((workingSpace / totalLabels) * gapRatio);

        // Calculate initial bar width and apply minimum
        let barWidth =
          Math.floor(workingSpace / totalLabels) - barGap - labelHeight;
        barWidth = Math.max(barWidth, minBarSize);

        // If bars would be too small, suggest scrollable container
        const needsScrolling =
          totalLabels > 0 &&
          workingSpace / totalLabels < minBarSize + barGap + labelHeight;
        const groupGap = Math.floor(
          (workingSpace / data.length) * (barGroupSpacing / 100)
        );
        const barPaddingPx = Math.floor(barWidth * (barPadding / 100));
        const rotationNeeded = barWidth < labelSize;
        const radians = rotationNeeded ? Math.acos(barWidth / labelSize) : 0;
        const angle = Math.round(0 - radians * (180 / Math.PI));
        const rise = Math.round(labelSize * Math.sin(radians) + labelHeight);
        const gdx = 0 - Math.min(radians * fontSize, fontSize * 0.75);
        const gdy = rise / 2 - labelHeight * 0.75;
        const xAxisHeight = Math.max(rise, labelHeight * 1.5);
        const rectY = -labelHeight;
        const rectX = Math.max(labelSize, barWidth) / -2;
        const rectWidth = Math.max(labelSize, barWidth);
        const rectHeight = labelHeight;
        const rectAngle = 180 + angle;
        const textAngle = angle;
        const textDy = labelHeight * 0.75;
        const textDx = 0;
        const ChartType =
          displayType === "lines"
            ? LineChart
            : displayType === "area"
            ? AreaChart
            : BarChart;

        // --- Dynamic label skipping calculation ---
        // Calculate optimal label spacing based on available space
        const availableSpace = vertical ? height : width;
        const estimatedLabelSize = vertical
          ? labelHeight
          : charLimit * charSize;

        // Dynamic label spacing based on container size and content
        const optimalLabelSpacing = Math.max(estimatedLabelSize * 1.2, 16);
        const maxFit = Math.floor(availableSpace / optimalLabelSpacing);

        // Adaptive skipping - scales smoothly with data density
        let skipInterval = 1;
        if (totalLabels > maxFit && maxFit > 0) {
          skipInterval = Math.ceil(totalLabels / maxFit);

          // Additional adjustment for very dense data
          if (totalLabels > 30) {
            skipInterval = Math.max(
              skipInterval,
              Math.ceil(totalLabels / Math.max(8, maxFit))
            );
          }
        }
        // ---------------------------------------------------

        // Calculate optimal height for dense data
        const calculatedHeight = needsScrolling
          ? Math.max(height, totalLabels * (minBarSize + barGap))
          : height;

        return (
          <div
            style={{
              height: height,
              width: width,
              overflowX: "hidden", // Prevent horizontal scrolling
              overflowY: needsScrolling ? "auto" : "hidden", // Allow vertical scrolling only when needed
              position: "relative",
            }}
          >
            <div
              style={{
                height: calculatedHeight,
                width: "100%",
                maxWidth: width,
                minHeight: needsScrolling
                  ? totalLabels * (minBarSize + barGap)
                  : "auto",
              }}
              onClick={(e) => {
                if (activeIdx < 0) return;
                if (!data[activeIdx]) return;
                const properties = [fromKey(xKey)];
                const values = [data[activeIdx][xKey]].map((val) => `${val}`);
                if (hoveredCategory && yKey) {
                  properties.push(fromKey(yKey));
                  values.push(hoveredCategory);
                } else if (legendPin && yKey) {
                  properties.push(fromKey(yKey));
                  values.push(legendPin);
                }
                popup.setPopup(
                  e,
                  <FilterMenu
                    accentColor={accentColor}
                    context={props.context}
                    values={values.map((v) => ({ value: v, label: v }))}
                    properties={properties}
                    formatters={[
                      (xVal) => debugFormatValue(xVal, xType.type, xType.unit),
                      (yVal) => debugFormatValue(yVal, yType.type, yType.unit),
                    ]}
                    open={filterOpen}
                    options={{
                      showDrillDown: true,
                      showFilters: true,
                      showGroupBy: true,
                      showPivot: true,
                      showSort: false,
                      showAggregate: true,
                      showEntityOpener: true,
                      showValueCopiers: false,
                    }}
                  />
                );
              }}
            >
              <ChartType
                layout={vertical ? "vertical" : "horizontal"}
                width={width}
                height={calculatedHeight}
                barCategoryGap={barGap}
                barGap={groupGap}
                data={data}
                onMouseLeave={() => {
                  handleBarMouseLeave();
                }}
                onMouseMove={(payload) => {
                  if (legendHighlight !== undefined) {
                    setLegendHighlight(undefined);
                  }
                  if (
                    payload &&
                    payload.activeTooltipIndex !== undefined &&
                    payload.activeTooltipIndex !== activeIdx
                  ) {
                    // this path is taken once when the activeId changes
                    setActiveIndex(payload.activeTooltipIndex);
                    // we close it once. it will reopen if needed
                    if (activeIdx !== -1) setFilterOpen(false);
                  } else {
                    const forcedClose = filterOpen === false;
                    // this is where we reopen it.
                    if (forcedClose) setFilterOpen(undefined);
                    if (forcedClose || payload.activeTooltipIndex === undefined)
                      setActiveIndex(-1);
                  }
                }}
              >
                {showGrid && (
                  <CartesianGrid
                    stroke={token.colorBorderSecondary}
                    horizontal={!vertical}
                    vertical={vertical}
                  />
                )}
                <XAxis
                  type={vertical ? "number" : "category"}
                  dataKey={vertical ? undefined : xKey}
                  strokeOpacity={0}
                  angle={angle}
                  height={xAxisHeight}
                  domain={data.length > 0 ? [minValue, maxValue] : undefined}
                  textAnchor="middle"
                  hide={(vertical && !showValueAxis) || (!vertical && !labels)}
                  axisLine={false}
                  minTickGap={20}
                  interval="preserveStartEnd"
                  tick={({
                    x,
                    y,
                    textAnchor,
                    tickFormatter,
                    payload: { index, value },
                  }) => (
                    <g
                      className="bar-chart-label"
                      transform={`translate(${x + gdx},${y + gdy})`}
                      onClickCapture={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(-1)}
                    >
                      <rect
                        transform={`rotate(${rectAngle})`}
                        y={rectY}
                        x={rectX}
                        width={rectWidth}
                        height={rectHeight}
                        rx="4"
                      />
                      <text
                        transform={`rotate(${textAngle})`}
                        dy={textDy}
                        dx={textDx}
                        fontSize={fontSize}
                        textAnchor={textAnchor}
                      >
                        {tickFormatter(
                          formatValue(value, xAxisType.type, xAxisType.unit)
                        )}
                      </text>
                    </g>
                  )}
                  tickFormatter={(value: string) => {
                    if (value === "__VZBL::null__") return "n/a";
                    // X-axis shows groupings (dates, categories), not values - don't apply timeUnit
                    const formattedValue = formatNumberAbbreviation(value);
                    if (`${formattedValue}`.length <= charLimit)
                      return formattedValue;
                    return `${formattedValue}`.slice(0, charLimit) + "…";
                  }}
                  tickLine={false}
                  padding={vertical ? { right: 64 } : undefined}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  type={vertical ? "category" : "number"}
                  hide={(!vertical && !showValueAxis) || (vertical && !labels)}
                  fontSize={fontSize}
                  minTickGap={10}
                  interval="preserveStartEnd"
                  width={
                    vertical ? Math.floor(width * 0.4) : labelSize + charSize
                  }
                  dataKey={vertical ? xKey : undefined}
                  padding={vertical ? undefined : { top: 24 }}
                  domain={data.length > 0 ? [minValue, maxValue] : undefined}
                  tickFormatter={(value: string) => {
                    if (value === "__VZBL::null__") return "n/a";
                    // Apply timeUnit only when Y-axis shows values (horizontal charts)
                    const axisType = vertical ? xAxisType : yAxisType;
                    const formattedValue =
                      vertical || !timeUnit
                        ? formatNumberAbbreviation(value)
                        : formatValue(
                            value,
                            axisType.type,
                            axisType.unit,
                            timeUnit
                          );
                    if (`${formattedValue}`.length <= charLimit)
                      return formattedValue;
                    return `${formattedValue}`.slice(0, charLimit) + "…";
                  }}
                  tick={({
                    x,
                    y,
                    textAnchor,
                    tickFormatter,
                    payload: { index, value },
                  }) => (
                    <g
                      className="bar-chart-label"
                      transform={`translate(${x},${y})`}
                      onClickCapture={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(-1)}
                    >
                      <rect
                        y={-labelHeight / 2}
                        x={-labelSize - charSize / 2}
                        width={labelSize + charSize}
                        height={labelHeight}
                        rx="4"
                      />
                      <text
                        dy={fontSize / 2.66}
                        dx={0}
                        fontSize={fontSize}
                        textAnchor={textAnchor}
                      >
                        {tickFormatter(
                          formatValue(
                            value,
                            yAxisType.type,
                            yAxisType.unit,
                            vertical ? undefined : timeUnit
                          )
                        )}
                      </text>
                    </g>
                  )}
                />
                <RechartsTooltip
                  cursor={{ fill: `${token.colorBgTextHover}88` }}
                  wrapperStyle={{ zIndex: 3 }}
                  content={(props: TooltipProps<number, string>) => (
                    <CustomTooltip
                      {...{
                        ...props,
                        labelKey: xKey,
                        xType,
                        yType,
                        zType,
                        valuePin: legendPin,
                        focusedStack: focusedStack,
                        hoveredCategory: hoveredCategory,
                        timeUnit: timeUnit,
                      }}
                    />
                  )}
                />
                {legend && (
                  <Legend
                    wrapperStyle={{ paddingTop: 4 }}
                    align={yKeys.length > 5 ? "right" : "center"}
                    content={({ payload = [] }) => (
                      <CustomLegend
                        setAccentColor={setAccentColor}
                        setHighlightValue={setLegendHighlight}
                        pinHighlightValue={setLegendPin}
                        highlightValue={legendHighlightx}
                        pinValue={legendPin}
                        payload={payload}
                        width={legendLabelWidth}
                        yType={yType}
                        yKeys={yKeys}
                        timeUnit={timeUnit}
                      />
                    )}
                  />
                )}
                {displayType === "lines"
                  ? sortedKeys.map((dataKey, i) => (
                      <Line
                        key={`${i}-${dataKey}`}
                        dataKey={dataKey!}
                        animationBegin={0}
                        animationDuration={0}
                        type={curveType}
                        radius={1}
                        strokeWidth={2}
                        strokeOpacity={
                          dataKey === legendHighlight || !legendHighlight
                            ? 1
                            : 0.1
                        }
                        stroke={
                          dataKey === legendHighlight || !legendHighlight
                            ? rainbowFn(dataKey!)
                            : rainbowFn(dataKey!, true)
                        }
                      >
                        {(legendHighlight === dataKey ||
                          (!legendHighlight && values)) && (
                          <LabelList
                            dataKey={dataKey}
                            position={vertical ? "right" : "top"}
                            fill={token.colorTextSecondary}
                            fontSize={12}
                            {...labelListRender({
                              color: token.colorTextSecondary,
                              asPercent,
                              vertical,
                              dataKey,
                              dataLabels,
                              legendHighlight,
                              xAxisType,
                              yAxisType,
                              data,
                              skipInterval,
                              timeUnit: timeUnit,
                            })}
                          />
                        )}
                      </Line>
                    ))
                  : displayType === "area"
                  ? sortedKeys.map((dataKey, i) => (
                      <Area
                        key={`${i}-${dataKey}`}
                        stackId={bars === "stack" ? stackId : undefined}
                        dataKey={dataKey!}
                        animationBegin={0}
                        animationDuration={0}
                        fill={
                          dataKey === legendHighlight || !legendHighlight
                            ? rainbowFn(dataKey!)
                            : rainbowFn(dataKey!, true)
                        }
                        stroke={
                          dataKey === legendHighlight || !legendHighlight
                            ? rainbowFn(dataKey!)
                            : rainbowFn(dataKey!, true)
                        }
                        type={curveType}
                        radius={1}
                        strokeOpacity={
                          dataKey === legendHighlight || !legendHighlight
                            ? 1
                            : 0.3
                        }
                        opacity={
                          dataKey === legendHighlight || !legendHighlight
                            ? 1
                            : 0.3
                        }
                        strokeWidth={2}
                      >
                        {(legendHighlight === dataKey ||
                          (!legendHighlight && values)) && (
                          <LabelList
                            dataKey={dataKey}
                            position={vertical ? "right" : "top"}
                            fill={token.colorTextSecondary}
                            fontSize={12}
                            {...labelListRender({
                              color: token.colorTextSecondary,
                              asPercent,
                              vertical,
                              dataKey,
                              dataLabels,
                              legendHighlight,
                              xAxisType,
                              yAxisType,
                              data,
                              skipInterval,
                              timeUnit: timeUnit,
                            })}
                          />
                        )}
                      </Area>
                    ))
                  : sortedKeys.map((dataKey, i) => {
                      const isStackFocused = focusedStack === i;
                      const isStackHighlighted = dataKey === legendHighlight;
                      const isHovered = hoveredCategory === dataKey;
                      const isInHoveredGroup =
                        hoveredGroupIndex !== null && hoveredGroupIndex >= 0;

                      // Get the current group's data for sorting
                      const currentGroupData =
                        data[hoveredGroupIndex ?? 0] || data[0];
                      const currentValue =
                        Number(currentGroupData?.[dataKey]) || 0;

                      // Determine opacity and style based on hover state
                      const getStyles = () => {
                        // If we're hovering, this takes precedence over legend highlights
                        if (hoveredCategory || hoveredGroupIndex !== null) {
                          if (isHovered && isInHoveredGroup) {
                            return {
                              opacity: 1,
                              strokeWidth: 2,
                              strokeOpacity: 1,
                              stroke: token.colorBorder,
                            };
                          }
                          if (isInHoveredGroup) {
                            return {
                              opacity: 1,
                              strokeWidth: barBorderWidth,
                              strokeOpacity: 1,
                              stroke: token.colorBgContainer,
                            };
                          }
                          if (isHovered) {
                            return {
                              opacity: 1,
                              strokeWidth: barBorderWidth,
                              strokeOpacity: 1,
                              stroke: token.colorBgContainer,
                            };
                          }
                          return {
                            opacity: 0.2,
                            strokeWidth: barBorderWidth,
                            strokeOpacity: 0.2,
                            stroke: token.colorBgContainer,
                          };
                        }

                        return {
                          opacity:
                            isStackHighlighted || !legendHighlight ? 1 : 0.2,
                          strokeWidth: barBorderWidth,
                          strokeOpacity:
                            isStackHighlighted || !legendHighlight ? 1 : 0.2,
                          stroke: token.colorBgContainer,
                        };
                      };

                      const styles = getStyles();
                      const shouldHighlight =
                        isStackFocused ||
                        isStackHighlighted ||
                        !legendHighlight;

                      return (
                        <Bar
                          key={`${dataKey}`}
                          dataKey={dataKey}
                          stackId={bars === "stack" ? stackId : undefined}
                          animationBegin={0}
                          animationDuration={200}
                          isAnimationActive={true}
                          onMouseEnter={(e) => {
                            const groupIndex = e.activeTooltipIndex ?? -1;
                            setHoveredGroupIndex(groupIndex);
                            setHoveredCategory(dataKey);
                            handleBarMouseEnter(dataKey, i);
                          }}
                          onMouseLeave={() => {
                            setHoveredGroupIndex(null);
                            setHoveredCategory(null);
                            handleBarMouseLeave();
                          }}
                          fill={
                            shouldHighlight
                              ? rainbowFn(dataKey)
                              : rainbowFn(dataKey, true)
                          }
                          opacity={styles.opacity}
                          stroke={styles.stroke}
                          radius={barBorderRadius}
                          strokeWidth={styles.strokeWidth}
                          maxBarSize={barWidth - barPaddingPx}
                          minPointSize={minSegmentSize}
                          strokeOpacity={styles.strokeOpacity}
                          style={{
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                        >
                          {shouldHighlight &&
                            values &&
                            (bars !== "stack" || sortedKeys.length === 1) && (
                              <LabelList
                                dataKey={dataKey}
                                position={vertical ? "right" : "top"}
                                {...labelListRender({
                                  color: token.colorTextSecondary,
                                  asPercent,
                                  vertical,
                                  dataKey,
                                  dataLabels,
                                  legendHighlight,
                                  xAxisType,
                                  yAxisType,
                                  data,
                                  skipInterval,
                                  timeUnit: timeUnit,
                                })}
                              />
                            )}
                        </Bar>
                      );
                    })}
              </ChartType>
            </div>
          </div>
        );
      }}
    </DynamicCanvas>
  );
}

export function BarChartControls(props: { context: ExploreContext }) {
  const { params, view, aggregations } = props.context;
  const { token } = theme.useToken();
  const config = toConfig(view.config);
  const countGroupBys = params.selectors.countGroupBys(params.state);
  const countAggregations = params.selectors.countAggregations(params.state);

  const handleSliderChange = (key: keyof Config, value: number) => {
    view.setConfig({ ...config, [key]: value });
  };

  const columns = (params.state.groupBys || []).map((g) => ({
    label: asKey(g),
    value: asKey(g),
  }));
  if (params.state.aggregations?.length)
    columns.push({
      label: columnForAggregation(params.state.aggregations!.at(0)!),
      value: "aggregation",
    });
  else {
    const impliedAgg = Object.keys(aggregations.data?.data?.at(0) || {}).pop();
    if (impliedAgg)
      columns.push({
        label: impliedAgg,
        value: impliedAgg,
      });
  }

  const hasCompareWith = params.state.compareWith?.method === "tuple";

  return (
    <Flex start direction="column" style={{ gap: 0 }}>
      {config.displayType !== "lines" && (
        <div className="section">
          <div className="section-header">Layout</div>
          {/* Chart Type & Variation */}
          <div style={{ marginBottom: 16 }}>
            <div className="layout-control-grid">
              <Button
                type="default"
                onClick={() =>
                  view.setConfig({
                    ...config,
                    bars: "stack",
                    asPercent: false,
                  })
                }
                className={`layout-control-button ${
                  config.bars === "stack" && !config.asPercent ? "selected" : ""
                }`}
              >
                <BarChartOutlined />
                <span className="layout-control-button-label">Stack</span>
              </Button>
              <Button
                type="default"
                onClick={() =>
                  view.setConfig({
                    ...config,
                    bars: "split",
                    asPercent: false,
                  })
                }
                className={`layout-control-button ${
                  config.bars === "split" && !config.asPercent ? "selected" : ""
                }`}
              >
                <svg
                  className="layout-control-button-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="4"
                    width="4"
                    height="16"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="10"
                    y="8"
                    width="4"
                    height="12"
                    rx="1"
                    fill="currentColor"
                    fillOpacity="0.7"
                  />
                  <rect
                    x="16"
                    y="12"
                    width="4"
                    height="8"
                    rx="1"
                    fill="currentColor"
                    fillOpacity="0.4"
                  />
                </svg>
                <span className="layout-control-button-label">
                  {config.displayType === "area" ? "Overlap" : "Split"}
                </span>
              </Button>
              <Button
                type="default"
                onClick={() =>
                  view.setConfig({
                    ...config,
                    asPercent: true,
                    bars: "stack",
                  })
                }
                className={`layout-control-button ${
                  config.asPercent ? "selected" : ""
                }`}
              >
                <PercentageOutlined />
                <span className="layout-control-button-label">Percent</span>
              </Button>
            </div>
          </div>
          <Flex direction="column" gap={8}>
            <Flex spaceBetween style={{ width: "100%" }}>
              <span
                style={{
                  fontSize: "14px",
                  color: token.colorText,
                }}
              >
                <SwapOutlined /> Invert Axis
              </span>
              <Switch
                checked={config.invertGroups}
                disabled={countGroupBys < 2}
                onChange={(checked) =>
                  view.setConfig({ ...config, invertGroups: checked })
                }
                size="small"
              />
            </Flex>
          </Flex>
        </div>
      )}
      {/* Display Options Section */}
      <div className="section">
        <div className="section-header">Style</div>
        <div className="layout-control-grid">
          <AntdTooltip title="Show Labels">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, labels: !config.labels })
              }
              className={`layout-control-button ${
                config.labels ? "selected" : ""
              }`}
            >
              <FontSizeOutlined />
              <span className="layout-control-button-label">Labels</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Show Legend">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, legend: !config.legend })
              }
              className={`layout-control-button ${
                config.legend ? "selected" : ""
              }`}
            >
              <svg
                className="layout-control-button-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="4"
                  y="6"
                  width="3"
                  height="3"
                  rx="0.5"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="11"
                  width="3"
                  height="3"
                  rx="0.5"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="16"
                  width="3"
                  height="3"
                  rx="0.5"
                  fill="currentColor"
                />
                <path
                  d="M9 7.5h11M9 12.5h11M9 17.5h11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="layout-control-button-label">Legend</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Show Grid">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, showGrid: !config.showGrid })
              }
              className={`layout-control-button ${
                config.showGrid ? "selected" : ""
              }`}
            >
              <TableOutlined />
              <span className="layout-control-button-label">Grid</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Show Values">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, values: !config.values })
              }
              className={`layout-control-button ${
                config.values ? "selected" : ""
              }`}
            >
              <NumberOutlined />
              <span className="layout-control-button-label">Values</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Show Axis">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({
                  ...config,
                  showValueAxis: !config.showValueAxis,
                })
              }
              className={`layout-control-button ${
                config.showValueAxis ? "selected" : ""
              }`}
            >
              <DragOutlined />
              <span className="layout-control-button-label">Show Axis</span>
            </Button>
          </AntdTooltip>
        </div>
      </div>
      {/* Color Theme Section */}
      <div style={{ marginTop: 0, width: "100%" }}>
        <ColorRampPicker
          ramp={config.theme}
          onChangeRamp={(theme) => view.setConfig({ ...config, theme })}
        />
      </div>
      {/* Advanced Style Controls */}
      <Collapse
        className="advanced-collapse"
        style={{ padding: "0" }}
        ghost
        expandIcon={({ isActive }) => (
          <CaretRightOutlined
            rotate={isActive ? 90 : 0}
            style={{
              fontSize: 10,
              color: "var(--color-text-tertiary)",
              paddingLeft: 0,
            }}
          />
        )}
      >
        <Collapse.Panel
          header={
            <span style={{ color: "var(--color-text-tertiary)", fontSize: 13 }}>
              Advanced Layout Settings
            </span>
          }
          key="1"
          style={{ padding: 0, border: "none" }}
        >
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {config.bars === "stack" && (
                <Flex spaceBetween style={{ width: "100%" }}>
                  <span
                    style={{
                      fontSize: "14px",
                      color: token.colorText,
                    }}
                  >
                    Order
                  </span>
                  <Select
                    size="small"
                    value={config.stackOrder}
                    style={{ width: 120 }}
                    onChange={(value) =>
                      view.setConfig({ ...config, stackOrder: value })
                    }
                    options={[
                      { label: "Bottom Up", value: "bottom-up" },
                      { label: "Top Down", value: "top-down" },
                    ]}
                  />
                </Flex>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="viz-label">Group Spacing</span>
              <Slider
                value={config.barGroupSpacing}
                onChange={(value) =>
                  handleSliderChange("barGroupSpacing", value)
                }
                min={0}
                max={100}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="viz-label">Bar Padding</span>
              <Slider
                value={config.barPadding}
                onChange={(value) => handleSliderChange("barPadding", value)}
                min={0}
                max={100}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="viz-label">Border Radius</span>
              <Slider
                value={config.barBorderRadius}
                onChange={(value) =>
                  handleSliderChange("barBorderRadius", value)
                }
                min={0}
                max={20}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="viz-label">Border Width</span>
              <Slider
                value={config.barBorderWidth}
                onChange={(value) =>
                  handleSliderChange("barBorderWidth", value)
                }
                min={0}
                max={5}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="viz-label">Min Segment Size</span>
              <Slider
                value={config.minSegmentSize}
                onChange={(value) =>
                  handleSliderChange("minSegmentSize", value)
                }
                min={0}
                max={20}
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <Flex spaceBetween style={{ width: "100%" }}>
            <span className="viz-label">Curve Type</span>
            <Select
              popupMatchSelectWidth={false}
              disabled={config.displayType === "bars"}
              options={curves.map((curve) => ({
                label: (
                  <span className="viz-select-option">
                    {attributeToTitle(curve)}
                  </span>
                ),
                value: curve,
              }))}
              bordered={false}
              className="viz-select"
              value={config.curveType}
              onChange={(val) => {
                view.setConfig({ ...config, curveType: val });
              }}
            />
          </Flex>
        </Collapse.Panel>
      </Collapse>

      <div className="section">
        <div className="section-header">Data</div>
        <div style={{ width: "100%", marginTop: 8 }}>
          <SearchWindowInput
            limit={config.limit}
            setLimit={(limit: number) => view.setConfig({ ...config, limit })}
            sortBy={config.sortBy}
            setSortBy={(sortBy: "ascending" | "descending") =>
              view.setConfig({ ...config, sortBy })
            }
            sortDirection={config.sortDirection}
            setSortDirection={(sortDirection: string) =>
              view.setConfig({ ...config, sortDirection })
            }
            columns={columns}
          />
        </div>
        {hasCompareWith && (
          <div style={{ width: "100%" }}>
            <CompareWithControls
              disabled={!hasCompareWith}
              showChangeAs={config.showChangeAs}
              onChangeShowChangeAs={(showChangeAs) =>
                view.setConfig({ ...config, showChangeAs })
              }
            />
          </div>
        )}
        {hasTimeUnit(props.context.aggregations) && (
          <div style={{ width: "100%" }}>
            <FormattingControls
              disabled={!hasTimeUnit(props.context.aggregations)}
              timeUnit={config.timeUnit}
              onChangeTimeUnit={(timeUnit) =>
                view.setConfig({ ...config, timeUnit })
              }
            />
          </div>
        )}
      </div>
    </Flex>
  );
}

type TypeInfo = { type: string; unit?: string };

const CustomLegend = ({
  payload,
  width,
  highlightValue,
  pinHighlightValue,
  pinValue,
  setAccentColor,
  setHighlightValue,
  yType,
  yKeys,
  timeUnit,
}: {
  width: number | undefined;
  highlightValue: string | undefined;
  pinValue: string | undefined;
  setAccentColor: Dispatch<SetStateAction<string | undefined>>;
  setHighlightValue: Dispatch<SetStateAction<string | undefined>>;
  pinHighlightValue: Dispatch<SetStateAction<string | undefined>>;
  payload: LegendProps["payload"];
  yType: TypeInfo;
  yKeys: string[];
  timeUnit?: TimeUnit;
}) => {
  const { token } = theme.useToken();
  useClickAnyWhere(() => setHighlightValue(() => undefined));
  return (
    <div
      className={`bar-chart-legend ${
        typeof highlightValue === "string" ? "open" : ""
      }`}
      style={{ height: 32 }}
    >
      <Flex
        start={!Boolean(pinValue)}
        wrap
        style={{
          gap: "1px 4px",
          padding: "2px 4px",
          maxWidth: "100%",
          justifyContent: yKeys.length > 10 ? "flex-start" : "center",
          minHeight: 28,
        }}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseOut={() => setHighlightValue(() => undefined)}
        onClick={() => {
          setHighlightValue(() => undefined);
          pinHighlightValue(() => undefined);
          setAccentColor(() => undefined);
        }}
      >
        {(payload || []).map((entry) =>
          (pinValue && pinValue === entry.value) || !pinValue ? (
            <div
              key={entry.value}
              onMouseMove={(e) => {
                if (!pinValue) {
                  e.stopPropagation();
                  setHighlightValue(() => `${entry.value}`);
                }
              }}
              onMouseOut={(e) => {
                e.stopPropagation();
                setHighlightValue((val) => (val === undefined ? val : ""));
              }}
              onClick={(e) => {
                e.stopPropagation();
                setHighlightValue(() => undefined);
                pinHighlightValue(() => entry.value);
                setAccentColor(() => entry.color);
              }}
              style={{
                alignItems: "center",
                alignSelf: "center",
                backgroundColor: [pinValue, highlightValue].includes(
                  entry.value
                )
                  ? entry.color
                  : "transparent",
                cursor: "default",
                borderRadius: 2,
                color: [pinValue, highlightValue].includes(entry.value)
                  ? token.colorTextLightSolid
                  : token.colorTextSecondary,
                display: "inline-flex",
                fontSize: 11,
                gap: 3,
                height: 14,
                maxWidth: yKeys.length > 10 ? 150 : 200,
                minWidth: 50,
                overflow: "hidden",
                padding: "0px 3px",
                textAlign: "left",
                textOverflow: "ellipsis",
                verticalAlign: "top",
                whiteSpace: "nowrap",
                width: width ? width + 16 : "auto",
              }}
            >
              <div
                style={{
                  height: 7,
                  width: 7,
                  marginLeft: 2,
                  backgroundColor: [pinValue, highlightValue].includes(
                    entry.value
                  )
                    ? token.colorTextLightSolid
                    : entry.color,
                  borderRadius: 4,
                }}
              />
              <div className="trunc">
                {valueToLabel(
                  debugFormatValue(
                    entry.value,
                    yType.type,
                    yType.unit,
                    timeUnit
                  )
                )}
              </div>
            </div>
          ) : null
        )}
        {pinValue && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setAccentColor(() => undefined);
              pinHighlightValue(() => undefined);
              setHighlightValue(() => "");
            }}
            type="text"
            shape="circle"
            size="small"
            icon={<ClearOutlined />}
            style={{ marginTop: 0, marginLeft: -4, height: 14, width: 14 }}
          />
        )}
      </Flex>
    </div>
  );
};

const CustomTooltip = ({
  active,
  label,
  labelKey,
  xType,
  yType,
  zType,
  valuePin,
  focusedStack,
  hoveredCategory,
  timeUnit,
  ...props
}: TooltipProps<number, string> & {
  valuePin?: string;
  labelKey: string;
  xType: TypeInfo;
  yType: TypeInfo;
  zType: TypeInfo;
  focusedStack?: number | null;
  hoveredCategory?: string | null;
  timeUnit?: TimeUnit;
}) => {
  const { token } = theme.useToken();
  const payload = [...(props.payload || [])].reverse();

  if (active && payload.length > 0) {
    const total = payload.reduce(
      (sum, entry) => sum + (Number(entry.value) || 0),
      0
    );

    const hoveredSegment = hoveredCategory
      ? payload.find((p) => p.name === hoveredCategory)
      : null;

    return (
      <div
        style={{
          backgroundColor: token.colorBgSpotlight,
          borderRadius: 4,
          boxShadow: token.boxShadowTertiary,
          color: token.colorTextLightSolid,
          display: "flex",
          flexDirection: "column",
          fontSize: 12,
          gap: 4,
          padding: 8,
          minWidth: 180,
        }}
      >
        <div
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            color: token.colorTextLightSolid,
          }}
        >
          {attributeToTitle(labelKey)}
        </div>
        <div
          className="trunc"
          style={{
            fontWeight: 600,
            maxWidth: 200,
            marginBottom: 5,
            color: token.colorTextLightSolid,
          }}
        >
          {valueToLabel(
            debugFormatValue(label, xType.type, xType.unit, timeUnit)
          )}
        </div>

        {hoveredSegment ? (
          <div
            style={{
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              paddingTop: 4,
              color: token.colorTextLightSolid,
            }}
          >
            <Flex
              spaceBetween
              direction={undefined}
              start
              gap={6}
              style={{ marginBottom: 4 }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  backgroundColor: hoveredSegment.color,
                }}
              />
              <span
                className="trunc"
                style={{
                  maxWidth: 120,
                  textAlign: "left",
                  color: token.colorTextLightSolid,
                }}
              >
                {valueToLabel(
                  debugFormatValue(
                    hoveredSegment.name,
                    yType.type,
                    yType.unit,
                    timeUnit
                  )
                )}
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color: token.colorTextLightSolid,
                }}
              >
                {debugFormatValue(
                  hoveredSegment.value,
                  zType.type,
                  zType.unit,
                  timeUnit
                )}
              </span>
            </Flex>
            <div
              style={{
                fontSize: 11,
                color: `${token.colorTextLightSolid}88`,
                textAlign: "right",
              }}
            >
              {Math.round((Number(hoveredSegment.value) / total) * 100)}% of
              stack
            </div>
          </div>
        ) : (
          payload.map(({ name, color, value }) =>
            !valuePin || valuePin === name ? (
              <Flex
                key={`${name}`}
                spaceBetween
                direction={undefined}
                start
                gap={6}
                style={{ marginBottom: 0 }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: color,
                  }}
                />
                <span
                  className="trunc"
                  style={{
                    maxWidth: 120,
                    textAlign: "left",
                    color: token.colorTextLightSolid,
                  }}
                >
                  {valueToLabel(
                    debugFormatValue(name, yType.type, yType.unit, timeUnit)
                  )}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    color: token.colorTextLightSolid,
                  }}
                >
                  {debugFormatValue(value, zType.type, zType.unit, timeUnit)}
                </span>
              </Flex>
            ) : null
          )
        )}

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: `${token.colorTextLightSolid}88`,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 4,
          }}
        >
          Stack Total:{" "}
          {debugFormatValue(total, zType.type, zType.unit, timeUnit)}
        </div>
      </div>
    );
  }
  return null;
};

const labelListRender = ({
  asPercent,
  dataLabels,
  dataKey,
  legendHighlight,
  vertical,
  xAxisType,
  yAxisType,
  color,
  data,
  skipInterval,
  timeUnit,
}: {
  legendHighlight?: string;
  dataKey: string;
  dataLabels: Record<string, string | undefined>[];
  asPercent: boolean;
  vertical: boolean;
  xAxisType: { type: string; unit: string };
  yAxisType: { type: string; unit: string };
  color: string;
  data: any[];
  skipInterval: number;
  timeUnit?: TimeUnit;
}): Partial<ComponentProps<typeof LabelList>> => ({
  content: (
    props:
      | any
      | {
          index: number;
          position?: string;
          width?: number;
          height?: number;
          viewBox: any;
        }
  ) => {
    // --- Skip labels based on calculated interval ---
    if (skipInterval > 1 && props.index % skipInterval !== 0) {
      return null;
    }

    const onTop = props.position !== "right";
    const onRight = !onTop;
    const formatted = props.formatter(props.value);

    if (!formatted || formatted === "" || formatted === "-") {
      return null;
    }

    const label =
      (dataLabels[props.index] && dataLabels[props.index][dataKey]) || "";
    const labeled = label ? `${formatted} (${label})` : formatted;
    const size = getTextSize(labeled, 12, 500);

    let finalX, finalY, haloX, haloY;
    const haloPadding = 2;
    const haloWidth = size.width + haloPadding * 2;
    const haloHeight = size.height + haloPadding * 2;

    if (vertical) {
      // --- Vertical Bar Chart Positioning ---
      // Position label to the right of the bar, vertically centered
      finalX = props.x + (props.width || 0) + 8; // x=bar_end + padding
      finalY = props.y + (props.height || 0) / 2; // y=bar_midpoint

      // Position halo centered around the text
      haloX = finalX - haloPadding; // Halo starts slightly left of text
      haloY = finalY - haloHeight / 2; // Center halo vertically
      // -----------------------------------
    } else {
      // --- Horizontal Chart Positioning (Line/Area/Bar) ---
      // Use existing dynamic offset logic
      let dyOffset = -size.height - 4; // Default above
      const currentValue = Number(data[props.index]?.[dataKey]) || 0;
      const prevValue = Number(data[props.index - 1]?.[dataKey]) || 0;

      finalX = props.x + (props.width || 0) / 2; // Center horizontally
      finalY = props.y + dyOffset; // Apply dynamic vertical offset

      // Position halo centered around the text
      haloX = finalX - haloWidth / 2;
      haloY = finalY - haloHeight / 2; // Center halo vertically
      // -------------------------------------------------
    }

    return (
      <g>
        {/* Halo Background */}
        <rect
          x={haloX}
          y={haloY}
          width={haloWidth}
          height={haloHeight}
          rx={3}
          ry={3}
          fill="var(--color-bg-container)"
          fillOpacity={0.8}
          stroke="none"
        />
        {/* Label Text */}
        <text
          x={finalX}
          y={finalY}
          style={{
            fontSize: 12,
            fontWeight: 500,
            fill: color,
            pointerEvents: "none",
          }}
          textAnchor={vertical ? "start" : "middle"} // Adjust text anchor
          dominantBaseline="central"
        >
          <tspan>{formatted}</tspan>
          {label && (
            <>
              <tspan style={{ opacity: 0.5 }}>{" ("}</tspan>
              <tspan style={{ opacity: 0.8 }}>{label}</tspan>
              <tspan style={{ opacity: 0.5 }}>{")"}</tspan>
            </>
          )}
        </text>
      </g>
    );
  },
  formatter: (value: string) =>
    !value ||
    value === "0" ||
    value === "__VZBL::null__" ||
    (legendHighlight !== dataKey && legendHighlight)
      ? ""
      : vertical
      ? formatValue(
          value,
          xAxisType.type,
          asPercent ? "%" : xAxisType.unit,
          timeUnit
        )
      : formatValue(
          value,
          yAxisType.type,
          asPercent ? "%" : yAxisType.unit,
          timeUnit
        ),

  /*
    const axisType = vertical ? xAxisType : yAxisType;
    let valueToFormat = value;

    console.log("valueToFormat", valueToFormat);
    if (
      !asPercent &&
      axisType.type !== "string" &&
      axisType.type !== "date" &&
      axisType.type !== "interval"
    ) {
      valueToFormat = formatNumberAbbreviation(value);
    }

    return formatValue(
      valueToFormat,
      axisType.type,
      asPercent ? "%" : axisType.unit
    );
    
  },*/
});
